import { defineStore, acceptHMRUpdate } from 'pinia';
import { $canSub } from '@pendo/permissions';
import {
    getPortals,
    createPortal,
    updatePortal,
    getPortal,
    deletePortal,
    duplicatePortal,
    getPortalIdeas,
    uploadPortalLogo,
    getPortalState,
} from '@/stores/api/voc-portal.api';
import { convertSvgFileToPngFile, getImageFileType, getSvgFileDimensions, getSvgRasterDimensions, SVG_DEFAULT_INTRINSIC_SIZE } from '@/common/image-upload/image-upload.utils';

interface Portal {
    id: string;
    internalName: string;
    pages?: PortalPage[];
    fetchedIdeas?: boolean;
    shareDetails?: unknown;
    feedbackFormShowImage?: boolean;
    [key: string]: unknown;
}

interface PortalPage {
    id?: string;
    itemLists?: PortalItemList[];
    [key: string]: unknown;
}

interface PortalItemList {
    id?: string;
    cards?: unknown[];
    [key: string]: unknown;
}

interface VocPortalState {
    map: Record<string, Portal>;
    error: unknown;
    activePortalId: string | null;
}

const promises: Record<string, Promise<unknown> | null> = {};

export function __resetPromisesForUnitTests () {
    for (const key of Object.keys(promises)) {
        promises[key] = null;
    }
}

export function getInitialState (): VocPortalState {
    return {
        map: {},
        error: {},
        activePortalId: null,
    };
}

export const useVocPortalStore = defineStore('vocPortal', {
    state: getInitialState,
    getters: {
        canCreatePortal: () => $canSub().entity('vocPortal').action('create'),
        canEditPortal: () => $canSub().entity('vocPortal').action('edit'),
        canDeletePortal: () => $canSub().entity('vocPortal').action('remove'),
        hasPortal: (state) => Object.keys(state.map).length > 0,
        portals: (state) =>
            Object.values(state.map).sort((a, b) =>
                a.internalName.toLowerCase().localeCompare(b.internalName.toLowerCase()),
            ),
        getPortalById: (state) => (portalId: string): Portal | undefined => state.map[portalId],
        activePortal: (state): Portal | undefined => state.map[state.activePortalId!],
        getPortalsByIds: (state) => (ids: string[]) => {
            if (!Array.isArray(ids)) return [];

            return ids
                .map((id) => state.map[id])
                .filter(Boolean)
                .map((portal) => ({
                    id: portal.id,
                    internalName: portal.internalName,
                }));
        },
    },
    actions: {
        setPortal (portalId: string, portal: Portal) {
            if (!portalId || !portal) return;
            this.map[portalId] = portal;
        },
        removePortal (portalId: string) {
            delete this.map[portalId];
        },
        setPortalShareDetails (portalId: string, portalShareDetails: unknown) {
            if (!portalId || !portalShareDetails) return;
            if (!this.map[portalId]) {
                this.map[portalId] = {} as Portal;
            }
            this.map[portalId].shareDetails = portalShareDetails;
        },
        setActivePortalId ({ portalId }: { portalId: string }) {
            this.activePortalId = portalId;
        },
        updatePortalIdeaPages (portalId: string, ideaPages: PortalPage[]) {
            if (!this.map[portalId]) return;
            const currentPortalPages = this.map[portalId].pages || [];
            const mergedPages = currentPortalPages.map((page, pageIndex) => {
                const matchedPage = ideaPages.find((ideaPage) => ideaPage.id === page.id);
                const fallbackPage = !matchedPage && !ideaPages[pageIndex]?.id ? ideaPages[pageIndex] : null;
                const fetchedPage = matchedPage || fallbackPage || {};
                const fetchedItemLists = (fetchedPage as PortalPage).itemLists || [];
                const itemLists = (page.itemLists || []).map((itemList, itemListIndex) => {
                    const matchedItemList = fetchedItemLists.find((list) => list.id === itemList.id);
                    const fallbackItemList = !matchedItemList && !fetchedItemLists[itemListIndex]?.id
                        ? fetchedItemLists[itemListIndex]
                        : null;
                    const fetchedItemList = matchedItemList || fallbackItemList || {};

                    return {
                        ...itemList,
                        cards: (fetchedItemList as PortalItemList).cards || [],
                    };
                });

                return { ...page, itemLists };
            });
            this.map[portalId].fetchedIdeas = true;
            this.map[portalId].pages = mergedPages;
        },
        async loadPortals () {
            if (promises.loadPortals) {
                return promises.loadPortals;
            }

            const loadPortalsPromise = getPortals()
                .then((portals: Portal[]) => {
                    portals.forEach((portal: Portal) => {
                        if (!this.map[portal.id]) {
                            this.setPortal(portal.id, portal);
                        }
                    });

                    return portals;
                })
                .catch((error) => {
                    this.error = error;
                    throw error;
                })
                .finally(() => {
                    promises.loadPortals = null;
                });

            promises.loadPortals = loadPortalsPromise;

            return loadPortalsPromise;
        },
        async loadPortalById ({ portalId, refetch = false }: { portalId: string; refetch?: boolean }) {
            if (refetch || !this.map[portalId]) {
                try {
                    const portal = await getPortal(portalId);
                    this.setPortal(portal.id, portal);
                } catch (error) {
                    this.error = error;
                    throw error;
                }
            }
            if (refetch || !this.map[portalId]?.fetchedIdeas) {
                try {
                    const portalIdeaConfig = await getPortalIdeas(portalId);
                    this.updatePortalIdeaPages(portalId, portalIdeaConfig);
                } catch (error) {
                    this.error = error;
                    throw error;
                }
            }
        },
        async createPortal (portal: Partial<Portal>) {
            try {
                const newPortal = await createPortal(portal);
                this.setPortal(newPortal.id, newPortal);

                return newPortal;
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async updatePortal ({ portal }: { portal: Portal }) {
            try {
                const currentPages = portal.pages;
                const updatedPortal = await updatePortal({ portalId: portal.id, portal });
                const mergedPortal: Portal = {
                    ...updatedPortal,
                    feedbackFormShowImage: updatedPortal.feedbackFormShowImage ?? portal.feedbackFormShowImage,
                };
                this.setPortal(mergedPortal.id, mergedPortal);
                if (currentPages !== undefined) {
                    this.updatePortalIdeaPages(updatedPortal.id, currentPages);
                }
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async deletePortal (portalId: string) {
            if (!portalId || !this.map[portalId]) return;
            try {
                await deletePortal(portalId);
                this.removePortal(portalId);
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async duplicatePortal ({ portal }: { portal: Portal }) {
            if (!portal.id) return;
            try {
                const duplicatedPortal = await duplicatePortal({ portalId: portal.id, portal });
                this.setPortal(duplicatedPortal.id, duplicatedPortal);
                await this.loadPortals();

                return duplicatedPortal;
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async uploadPortalLogo ({ imageFile, portalId }: { imageFile: File; portalId: string }) {
            try {
                let fileToUpload = imageFile;

                if (getImageFileType(imageFile) === 'image/svg+xml') {
                    const naturalDimensions =
                        (await getSvgFileDimensions(imageFile).catch(() => null)) || SVG_DEFAULT_INTRINSIC_SIZE;
                    fileToUpload = await convertSvgFileToPngFile(imageFile, getSvgRasterDimensions(naturalDimensions));
                }

                const logoImageResp = await uploadPortalLogo(portalId, fileToUpload);

                return logoImageResp.url;
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async getSharePortalDetails (portalId: string) {
            const portal = this.map[portalId];
            if (portal?.shareDetails) {
                return portal.shareDetails;
            }
            try {
                const portalShareDetails = await getPortalState(portalId);

                return portalShareDetails;
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useVocPortalStore, module.hot));
}
